import { Button, FormControlLabel, Pagination, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { sxButton, sxRadioGroup, sxTextField } from "./sxProps";
import './styles.css'
import { ChangeEvent, Fragment, useCallback, useEffect, useState } from "react";
import FilterSelect from "../../components/FilterSelect";
import MedicGuideButtons from "./MedicGuideButtons";
import { FilterInterface } from "../../interfaces/entities/FilterInterface";
import { buscarGuiaMedico, cadastroFavorito, getCidades, getEspecialidades, getTiposServiço, removeFavorito } from "../../services/SaudeOnlineService";
import LoadingApp from "../../components/LoadingApp";
import ModalFeedBack from "../../components/ModalFeedBack";
import { Doctor } from "../../interfaces/entities/Doctor";
import CardGuiaMedico from "./BuscaServicos/CardGuiaMedico";
import { sxPagination } from "./BuscaServicos/sxProps";
import { GuiaMedicoBody } from "../../interfaces/entities/GuiaMedicoBody";
import { debounce } from "../../utils/Debounce";
import { useAccessibility } from "../../Store/contexts/AccessibilityContext";
import { sxPaginationContrast } from "../../assets/styles/CommonSxProps";
import { scrollTo } from "../../utils/ScrollUtil";
import DefaultTextField from "../../components/DefaultTextField";
import PageTitle from "../../components/Accessibility/PageTitle";

function GuiaMedico() {

    const accessibility = useAccessibility();

    const [eraseFilter, setEraseFilter] = useState<boolean>(false);
    const [searchString, setSearchString] = useState<string>('');
    const [searchIn, setSearchIn] = useState<string>('medicGuide');
    
    const [cities, setCities] = useState<FilterInterface[]>([]);
    const [specialties, setSpecialties] = useState<FilterInterface[]>([]);
    const [servicesTypes, setServicesTypes] = useState<FilterInterface[]>([]);
    
    const [specialization, setSpecialization] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [serviceType, setServiceType] = useState<string>('');
    
    const [showLoading, setShowLoading] = useState<boolean>(false);
    const [showModalFeedBack, setShowModalFeedBack] = useState<boolean>(false);
    const [data, setData] = useState<Doctor[]>([]);

    const [showResults ,setShowResults] = useState<boolean>(false);
	const [sortSelected, setSortSelected] = useState<string | undefined>('nome');
    
    const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
    const [paginationCount, setPaginationCount] = useState<number>(1);
    const paginationLength = 5;
    const siblingCount = 2;

    useEffect(() => {
        const fetchData = async () => {
            setShowLoading(true)
            const cityResult = await getCidades();
            const especResult = await getEspecialidades();
            const serviceTypeResult = await getTiposServiço();
            if (cityResult !== null && especResult !== null && serviceTypeResult !== null) {
                const espec: FilterInterface[] = especResult;
                const citiesList: FilterInterface[] = cityResult;
                const servicesList: FilterInterface[] = serviceTypeResult;

                espec.unshift({ id: '-1', descricao: 'Em qualquer especialidade' });
                citiesList.unshift({ id: '-1', descricao: 'Em qualquer cidade' });
                servicesList.unshift({ id: '-1', descricao: 'Em qualquer serviço' });

                setCities(citiesList);
                setSpecialties(espec);
                setServicesTypes(servicesList);
                setShowLoading(false);
            }
        };
    
        fetchData();
    }, []);

    const groupByIdPessoa = (docs: Doctor[]) => {
        const map = new Map();
        for (const doc of docs) {
            if (map.has(doc.idPessoa)) {
                map.set(doc.idPessoa, [...map.get(doc.idPessoa), doc]);
            } else {
                map.set(doc.idPessoa, [doc]);
            }
        }
        return Array.from(map.values());
    }

    const handleSpecialization = (value: string) => {
        setSpecialization(value)
    }
    
    const handleCity = (value: string) => {
        setCity(value.toString())
    }

    const handleServiceType= (value: string) => {
        setServiceType(value.toString())
    }

    const handleSearchChange = async (event: ChangeEvent<HTMLInputElement | HTMLInputElement>) => {
        const value = event.target.value;
        setSearchString(value);
        if (value.length === 0 && !city && !specialization && !serviceType) {
            setShowResults(false);
        } else if (value.length >= 3 || value.length === 0) {
            getDataDebounced(1, value, sortSelected, city, specialization, serviceType, searchIn);
        }
    };

    const handleSearchIn = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchIn(event.target.value);
    };

    const handleCleanFilters = () => {
        setEraseFilter(!eraseFilter)
        setSearchString('');
        setSearchIn('medicGuide');
        setCity('');
        setSpecialization('');
        setServiceType('');
        setShowResults(false)
    }

    const getData = async (pageNumber: number, 
                           searchString: string | undefined, 
                           sortSelected: string | undefined,
                           city: string | undefined,
                           specialization: string | undefined,
                           serviceType: string | undefined,
                           searchIn: string | undefined) => {
        setShowLoading(true);
        setCurrentPageNumber(pageNumber);

        const tipoServico = searchIn === 'occupationalHealth' ? '05' : serviceType

        const searchFilter: GuiaMedicoBody = {
            tipoServico, 
            especialidade: specialization, 
            cidade: city,
            search: (searchString && searchString.length >= 3) ? searchString : undefined,
            start: (pageNumber - 1) * paginationLength,
            limit: getLimit(pageNumber),
            order: sortSelected
        }

        if (specialization === '' || specialization === '-1') {
            delete searchFilter.especialidade;
        }

        if (city === '' || city === '-1') {
            delete searchFilter.cidade;
        }

        if (tipoServico === '' || tipoServico === '-1') {
            delete searchFilter.tipoServico;
        }

        if(searchString === ''){
            delete searchFilter.nome;
        }

        const result = await buscarGuiaMedico(searchFilter);
        setShowLoading(false);
        setShowResults(true);
        setPaginationCount(Math.ceil(pageNumber + result.length/paginationLength - 1))
        setData(result.slice(0, paginationLength));
    }

  /* 
   * O useCallback evita que seja criada uma 
   * instância nova da função toda vez que renderiza.
   */
  const getDataDebounced = useCallback(debounce(getData, 400), []);

 /*
  * Essa lógica é pra buscar sempre o menor número necessário
  * de registros dado o comportamento do componente de paginação.
  */
  const getLimit = (pageNumber: number): number => {
    return paginationLength * Math.max(
      siblingCount + 3,
      (siblingCount * 2) + 5 - pageNumber
    )
  }

    const handleShowModalFeedBack = () => {
        setShowModalFeedBack(showModalFeedBack => !showModalFeedBack);
    }

    const toggleFavorito = (doctor: Doctor) => {
        (doctor.isFavorito ? removeFavorito : cadastroFavorito)(doctor.id);
        const newDados = data.map(d => {
            return d.id === doctor.id
            ? { ...d, isFavorito: !doctor.isFavorito }
            : d
        });
        setData(newDados);
    }

    const handlePageChange = (_: ChangeEvent<unknown>, newPage: number) => {
        setCurrentPageNumber(newPage);
        getData(newPage, searchString, sortSelected, city, specialization, serviceType, searchIn);
        scrollTo('scroll-ref');
    };

    const handleSortSelected = (sortSelected: string | undefined) => {
        setSortSelected(sortSelected);
        getData(currentPageNumber, searchString, sortSelected, city, specialization, serviceType, searchIn);
        setCurrentPageNumber(1);
    }

    const openUnimedGuide = () => {
        window.open('https://www.unimed.coop.br/site/guia-medico#/', '_blank');
    }

    return (
        <div className="medic-guide-holder">
            <LoadingApp showLoading={showLoading}/>
            <PageTitle text="Guia médico" marginBottom="16px" />
            <div className="medic-guide-content">
                <div className="medic-guide-header">
                    <div className="medic-guide-header-dropdown-row">
                        <div className="medic-guide-header-search">
                            <div className="medic-guide-search-holder">
                                <DefaultTextField
                                    labeled
                                    label="Pesquisar no guia médico"
                                    id="pesquisar"
                                    handleChange={handleSearchChange}
                                    value={searchString}
                                    placeholder="Exemplos: José (nome), cirurgião (especialidade) ou Rua 15 (endereço)"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="medic-guide-header-dropdown-row">
                        <div className="medic-guide-header-dropdown">
                            <FilterSelect 
                                title='Cidade' 
                                options={cities}
                                value={city}
                                handleChange={handleCity} 
                                erase={eraseFilter}
                                disabled={false}
                            />
                        </div>
                        <div className="medic-guide-header-dropdown">
                            <FilterSelect 
                                title='Tipo de serviço' 
                                options={servicesTypes} 
                                value={serviceType}
                                disabled={false} 
                                handleChange={handleServiceType} 
                                erase={eraseFilter}
                            />
                        </div>
                        <div className="medic-guide-header-dropdown">
                            <FilterSelect 
                                title='Especialidade' 
                                options={specialties}
                                value={specialization} 
                                disabled={false} 
                                handleChange={handleSpecialization} 
                                erase={eraseFilter}
                            />
                        </div>
                    </div>
                    <div className="medic-guide-header-item-row">
                        <div className="medic-guide-header-item">
                            <label id="radio-group-label" className='select-label' style={accessibility.textStyleAccessibility}>Procurar em:</label>
                            <RadioGroup
                                aria-labelledby="radio-group-label"
                                name="radio-buttons-group"
                                value={searchIn || 'medicGuide'}
                                onChange={handleSearchIn}
                                sx={sxRadioGroup}
                                row>
                                <FormControlLabel value={'medicGuide'} control={<Radio style={accessibility.contrastColorAccessibility} size='small'/>} label={<Typography style={{...accessibility.textStyleAccessibility, color: '#4C4C4C'}}>Guia Médico</Typography>} />
                                <FormControlLabel value={'occupationalHealth'} control={<Radio style={accessibility.contrastColorAccessibility} size='small'/>} label={<Typography style={{...accessibility.textStyleAccessibility, color: '#4C4C4C'}}>Saúde Ocupacional</Typography>} />
                            </RadioGroup>
                        </div>
                        <div className="medic-guide-header-buttom-row">
                            <div className="medic-guide-header-item">
                                <div className="medic-guide-filter-button">
                                    <Button 
                                        variant="contained" 
                                        size="large"
                                        onClick={() => getData(1, searchString, sortSelected, city, specialization, serviceType, searchIn)}
                                        sx={sxButton}
                                        style={accessibility.buttonStyleAccessibility}
                                        fullWidth>
                                            <span style={accessibility.buttonTextStyleAccessibility}>
                                                Aplicar filtros
                                            </span>
                                        
                                    </Button>
                                </div>
                            </div>
                            <div className="medic-guide-header-item">
                                <div className="medic-guide-filter-clean-button-holder">
                                    <button className="medic-guide-filter-clean-button" onClick={handleCleanFilters}>
                                        <u style={accessibility.textStyleAccessibility}>Limpar filtros</u>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="divider" />
                </div>
                {showResults && data.length > 0 &&
                    <>
                        <div className="sort-select" id="scroll-ref">
                            <FilterSelect 
                                title='Ordenar'
                                options={[
                                    { id: 'nome', descricao: 'Nome' }, 
                                    { id: 'dispon', descricao: 'Data de disponibilidade'}
                                ]}
                                disabled={false} 
                                handleChange={handleSortSelected} 
                            />
                        </div>
                        <ul style={{listStyleType: 'none', padding: '0px'}}>
                        {
                            groupByIdPessoa(data).map((doctors, i) =>
                                <li key={i}>
                                    <CardGuiaMedico
                                        dados={doctors}
                                        icone={'medico'}
                                        onFavoritoClicked={toggleFavorito}
                                        mostrarDisponibilidade={true}
                                    />
                                </li>
                            )
                        }
                        </ul>
                        {
                            !showLoading &&
                            <Pagination
                                className='pagination-holder'
                                sx={accessibility.contrastMode ? sxPaginationContrast : sxPagination}
                                color='primary'
                                page={currentPageNumber}
                                siblingCount={siblingCount}
                                boundaryCount={0}
                                count={paginationCount}
                                onChange={handlePageChange}
                          /> 
                        }
                    </> 
                }
                {showResults && data.length === 0 &&
                    <h1 className='empty-results' style={accessibility.textStyleAccessibility} tabIndex={0}>Nenhum registro encontrado.</h1>
                }
                {!showResults &&
                    <div className="medic-guide-body">
                        <MedicGuideButtons/>
                    </div>
                }
            </div>
            <ModalFeedBack
                showModal={showModalFeedBack}
                handleButtonModal={handleShowModalFeedBack}
                showButtonModal
                icon='error'
                handleShowModal={handleShowModalFeedBack}
                textButton='Voltar'
                title='Erro'
                subTitle='Por favor, selecione uma das opções de "Procurar em:"'
            />
        </div>
    )
}

export default GuiaMedico;